import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import AccessDenied from "./AccessDenied";
import LoadingScreen from "../loadingScreen/LoadingScreen";
import Main from "../../pages/Main";
import Projects from "../../pages/Projects";
import Settings from "../../pages/Settings";
import { Route, Routes } from "react-router";

import axios from "axios";
import {
  HOST_URL,
  PERSONALHUB_PAGE_EXTENSION,
  PROJECTDATA_URL,
  TOKEN_URL,
  HUB_ID,
  LOGOUT_URL,
  FETCH_LOGOUT_PENDING,
  FETCH_LOGOUT_SUCCESS,
  FETCH_LOGOUT_FAILED,
  LOGIN_PAGE,
  HELP_PAGE_EXTENSION,
  HOME_PAGE_EXTENSION,
} from "../../helpers/constants/Constants";

import { requestData } from "../../helpers/actions/RequestData";
import {
  USER_INFO_URL,
  FETCH_USER_DATA_PENDING,
  FETCH_USER_DATA_SUCCESS,
  FETCH_USER_DATA_FAILED,
  MAIN_PAGE_EXTENSION,
  VIEWER_PAGE_EXTENSION,
  SETTINGS_PAGE_EXTENSION,
} from "../../helpers/constants/Constants";
import MWHBootstrap from "../../pages/MWHBootstrap";
import PersonalHub from "../../pages/PersonalHub";
import {
  ALL_USER_SUBMITTALS,
  AUTH_TIMER,
  PERSONAL_HUB_NOTIFICATIONS,
  PH_REVIEWS_INFO,
  PH_REVIEWS_MARKUPS,
  PROJECT_LIST,
  SUPER_USER_DETAILS,
  updateGlobalState,
} from "../../helpers/reducers/GlobalReducer";
import { wipeState } from "../../helpers/actions/WipeState";
import Help from "../../pages/Help";
import Home from "../../pages/Home";
import NotFound from "./NotFound";

const REFRESH_BUFFER_MS = 15 * 60 * 1000;
const ACTIVITY_IDLE_MS = 45 * 60 * 1000;
const MIN_REFRESH_INTERVAL_MS = 60 * 1000;

const IfLoggedIn = () => {
  const { phReviewsInfo, phReviewsMarkups, allUserSubmittals, authTimer } =
    useSelector((state) => state.globalReducer);

  const userData = useSelector((state) => state.userData);

  // window.addEventListener("beforeunload", function () {
  //   dispatch(
  //     requestData(
  //       LOGOUT_URL,
  //       {
  //         pending: FETCH_LOGOUT_PENDING,
  //         success: FETCH_LOGOUT_SUCCESS,
  //         error: FETCH_LOGOUT_FAILED,
  //       },
  //       { withCredentials: true }
  //     )
  //   );
  // });
  const dispatch = useDispatch();
  const tokenExpiryAtRef = useRef(null);
  const lastActivityAtRef = useRef(Date.now());
  const lastRefreshAttemptAtRef = useRef(0);
  const refreshInFlightRef = useRef(false);
  const refreshTimeoutRef = useRef(null);
  const refreshForgeTokenSilentlyRef = useRef(async () => {});
  const bootstrappedUserEmailRef = useRef(null);

  const performClientLogout = useCallback(() => {
    dispatch(
      requestData(
        LOGOUT_URL,
        {
          pending: FETCH_LOGOUT_PENDING,
          success: FETCH_LOGOUT_SUCCESS,
          error: FETCH_LOGOUT_FAILED,
        },
        { withCredentials: true },
      ),
    );
    dispatch(wipeState(true));
    window.location.href = LOGIN_PAGE;
  }, [dispatch]);

  const updateAuthTimer = (expiry) => {
    tokenExpiryAtRef.current = Date.now() + expiry;
    dispatch(
      updateGlobalState({
        value: expiry,
        stateName: AUTH_TIMER,
      }),
    );
  };

  refreshForgeTokenSilentlyRef.current = async (force = false) => {
    if (refreshInFlightRef.current) {
      return false;
    }

    const now = Date.now();

    if (
      !force &&
      now - lastRefreshAttemptAtRef.current < MIN_REFRESH_INTERVAL_MS
    ) {
      return false;
    }

    refreshInFlightRef.current = true;
    lastRefreshAttemptAtRef.current = now;

    try {
      const tokenResponse = await axios.get(TOKEN_URL, {
        withCredentials: true,
      });
      const expiresInSeconds = Number(tokenResponse?.data?.expires_in);

      if (!Number.isFinite(expiresInSeconds) || expiresInSeconds <= 0) {
        throw new Error("Received invalid token expiry.");
      }

      updateAuthTimer(expiresInSeconds * 1000);
      return true;
    } catch (error) {
      console.log(error);
      return false;
    } finally {
      refreshInFlightRef.current = false;
    }
  };

  const setLogoutTimer = useCallback(
    (expiry) => {
      clearTimeout(window.warningTimer);
      clearTimeout(window.logoutTimer);

      const warningDelay = Math.max(expiry - 300000, 0);
      const logoutDelay = Math.max(expiry, 0);

      window.warningTimer = setTimeout(() => {
        alert("Your session expires soon. Keep working to stay signed in.");
      }, warningDelay);

      window.logoutTimer = setTimeout(async () => {
        const refreshed = await refreshForgeTokenSilentlyRef.current(true);
        if (refreshed) {
          return;
        }

        performClientLogout();
      }, logoutDelay);
    },
    [performClientLogout],
  );

  const calculateNotifications = useCallback(() => {
    let numberOfSupSubmittals =
      allUserSubmittals?.allSupplierRepSubmittals?.length;
    if (numberOfSupSubmittals === undefined) {
      numberOfSupSubmittals = 0;
    }
    let numberOfResSubmittals =
      allUserSubmittals?.allResponsiblePersonSubmittals?.length;
    if (numberOfResSubmittals === undefined) {
      numberOfResSubmittals = 0;
    }
    let numberOfChecks = phReviewsInfo.forCheck?.length;
    if (numberOfChecks === undefined) {
      numberOfChecks = 0;
    }
    let numberOfReviews = phReviewsInfo.forReview?.length;
    if (numberOfReviews === undefined) {
      numberOfReviews = 0;
    }
    let numberOfApprovals = phReviewsInfo.forApproval?.length;
    if (numberOfApprovals === undefined) {
      numberOfApprovals = 0;
    }
    let numberOfMarkups = (phReviewsMarkups || []).filter((markup) => {
      const normalizedStatus = String(markup?.status || "")
        .trim()
        .toLowerCase();
      const isClosed =
        normalizedStatus === "closed" || Boolean(markup?.actioned);
      const isArchived = Boolean(markup?.archived);

      return !isArchived && !isClosed;
    }).length;
    if (numberOfMarkups === undefined) {
      numberOfMarkups = 0;
    }
    const total =
      numberOfChecks +
      numberOfReviews +
      numberOfApprovals +
      numberOfMarkups +
      numberOfResSubmittals +
      numberOfSupSubmittals;

    dispatch(
      updateGlobalState({
        value: {
          total,
          numberOfChecks,
          numberOfReviews,
          numberOfApprovals,
          numberOfMarkups,
          numberOfSupSubmittals,
          numberOfResSubmittals,
        },
        stateName: PERSONAL_HUB_NOTIFICATIONS,
      }),
    );
  }, [allUserSubmittals, dispatch, phReviewsInfo, phReviewsMarkups]);

  useEffect(() => {
    dispatch(
      requestData(
        USER_INFO_URL,
        {
          pending: FETCH_USER_DATA_PENDING,
          success: FETCH_USER_DATA_SUCCESS,
          error: FETCH_USER_DATA_FAILED,
        },
        { withCredentials: true },
        // no callback
      ),
    );
  }, [dispatch]);

  useEffect(() => {
    if (authTimer) {
      tokenExpiryAtRef.current = Date.now() + authTimer;
      setLogoutTimer(authTimer);
    } else if (userData.data._tokenExpiresIn) {
      tokenExpiryAtRef.current =
        Date.now() + userData.data._tokenExpiresIn * 1000;
      setLogoutTimer(userData.data._tokenExpiresIn * 1000);
    }
  }, [authTimer, setLogoutTimer, userData]);

  useEffect(() => {
    if (!userData?.data?.loggedIn) {
      return undefined;
    }

    const isUserActive = () => {
      return (
        document.visibilityState === "visible" &&
        Date.now() - lastActivityAtRef.current < ACTIVITY_IDLE_MS
      );
    };

    const maybeRefreshToken = () => {
      if (!isUserActive() || !tokenExpiryAtRef.current) {
        return;
      }

      if (tokenExpiryAtRef.current - Date.now() <= REFRESH_BUFFER_MS) {
        refreshForgeTokenSilentlyRef.current();
      }
    };

    const recordActivity = () => {
      lastActivityAtRef.current = Date.now();
      maybeRefreshToken();
    };

    const recordVisibleActivity = () => {
      if (document.visibilityState === "visible") {
        recordActivity();
      }
    };

    window.addEventListener("pointermove", recordActivity, { passive: true });
    window.addEventListener("pointerdown", recordActivity, { passive: true });
    window.addEventListener("keydown", recordActivity);
    window.addEventListener("scroll", recordActivity, { passive: true });
    window.addEventListener("touchstart", recordActivity, { passive: true });
    document.addEventListener("visibilitychange", recordVisibleActivity);

    return () => {
      window.removeEventListener("pointermove", recordActivity);
      window.removeEventListener("pointerdown", recordActivity);
      window.removeEventListener("keydown", recordActivity);
      window.removeEventListener("scroll", recordActivity);
      window.removeEventListener("touchstart", recordActivity);
      document.removeEventListener("visibilitychange", recordVisibleActivity);
    };
  }, [userData?.data?.loggedIn]);

  useEffect(() => {
    if (!userData?.data?.loggedIn || !tokenExpiryAtRef.current) {
      return undefined;
    }

    const scheduleRefresh = () => {
      clearTimeout(refreshTimeoutRef.current);

      const delay = Math.max(
        tokenExpiryAtRef.current - Date.now() - REFRESH_BUFFER_MS,
        0,
      );

      refreshTimeoutRef.current = setTimeout(() => {
        const userIsActive =
          document.visibilityState === "visible" &&
          Date.now() - lastActivityAtRef.current < ACTIVITY_IDLE_MS;

        if (userIsActive) {
          refreshForgeTokenSilentlyRef.current();
        }
      }, delay);
    };

    scheduleRefresh();

    return () => {
      clearTimeout(refreshTimeoutRef.current);
    };
  }, [authTimer, userData?.data?.loggedIn]);

  useEffect(() => {
    const isLoggedIn = userData?.data?.loggedIn;
    const userEmail = userData?.data?.emailId;

    if (!isLoggedIn || !userEmail) {
      bootstrappedUserEmailRef.current = null;
      return;
    }

    if (bootstrappedUserEmailRef.current === userEmail) {
      return;
    }

    bootstrappedUserEmailRef.current = userEmail;

    axios
      .get(HOST_URL + "/forgeData/getuserdetails", {
        withCredentials: true,
        params: {
          userEmail,
        },
      })
      .then((data) => {
        if (data.data.message) {
        } else {
          dispatch(
            updateGlobalState({
              value: data.data,
              stateName: SUPER_USER_DETAILS,
            }),
          );
        }
      })
      .catch((err) => console.log(err));

    axios({
      method: "get",
      url: HOST_URL + "/projects/getUserSubmittals",
      params: { email: userEmail },
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      withCredentials: true,
    })
      .then((response) => {
        dispatch(
          updateGlobalState({
            value: response.data,
            stateName: ALL_USER_SUBMITTALS,
          }),
        );
      })
      .catch((error) => {
        console.log(error);
      });

    axios({
      method: "get",
      url: HOST_URL + "/phReviews/getUserReviews",
      params: { email: userEmail.toLowerCase() },
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      withCredentials: true,
    })
      .then((response) => {
        dispatch(
          updateGlobalState({
            value: response.data,
            stateName: PH_REVIEWS_INFO,
          }),
        );
      })
      .catch((error) => {
        console.log(error);
      });

    axios({
      method: "get",
      url: HOST_URL + "/phReviews/getUserMarkups",
      params: { email: userEmail.toLowerCase() },
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      withCredentials: true,
    })
      .then((response) => {
        dispatch(
          updateGlobalState({
            value: response.data,
            stateName: PH_REVIEWS_MARKUPS,
          }),
        );
      })
      .catch((error) => {
        console.log(error);
      });

    axios({
      method: "get",
      url: PROJECTDATA_URL,
      withCredentials: true,
      params: {
        type: "projects",
        hubId: HUB_ID,
      },
    })
      .then((data) => {
        const sortedData = data.data.sort((a, b) =>
          a.text.localeCompare(b.text),
        );
        dispatch(
          updateGlobalState({
            value: sortedData,
            stateName: PROJECT_LIST,
          }),
        );
      })
      .catch(console.log);
  }, [dispatch, userData?.data?.emailId, userData?.data?.loggedIn]);

  useEffect(() => {
    // debugger;

    calculateNotifications();
  }, [calculateNotifications]);

  if (userData.data.loggedIn === true && userData.pending === false) {
    return (
      <>
        {/* <Router> */}
        <Routes>
          <Route path={MAIN_PAGE_EXTENSION} element={<Main />} />
          <Route path={VIEWER_PAGE_EXTENSION} element={<Projects />} />

          {/* <Route
              
              path={VISUALISATION_PAGE_EXTENSION}
              element={<Visualisation/>}
            /> */}
          <Route path={PERSONALHUB_PAGE_EXTENSION} element={<PersonalHub />} />
          <Route path={SETTINGS_PAGE_EXTENSION} element={<Settings />} />
          {/* <Route  path={FIELD_PAGE_EXTENSION} element={<Field/>} /> */}
          {/* <Route
              
              path={PSH_PAGE_EXTENSION}
              element={<ProcessSafetyHub/>}
            /> */}
          {/* <Route  path={HAZOP_PAGE_EXTENSION} element={<Hazop/>} /> */}
          <Route path={HELP_PAGE_EXTENSION} element={<Help />} />
          <Route path={HOME_PAGE_EXTENSION} element={<Home />} />
          <Route path={"/bootstrap"} element={<MWHBootstrap />} />
          <Route path={"/mwh-bootstrap"} element={<MWHBootstrap />} />
          <Route element={<NotFound />} />
        </Routes>
        {/* </Router> */}
      </>
    );
  } else if (userData.data.loggedIn === false && userData.pending === true) {
    return (
      <>
        <LoadingScreen input="LOADING" variant="auth" />
      </>
    );
  } else if (userData.data.loggedIn === false && userData.pending === false) {
    return (
      <>
        <AccessDenied />
      </>
    );
  }
};

export default IfLoggedIn;
